import { useReveal } from '../hooks/useReveal.js';
import { DeviceFrame } from '../components/DeviceFrame.js';
import { PhoneShot } from '../components/PhoneShot.js';

type Moment = {
  time: string;
  title: string;
  body: string;
  shot: { src: string; alt: string };
};

const moments: Moment[] = [
  {
    time: 'Before Dawn',
    title: 'Morning Prayers',
    body: 'The first words of the day are not your own. The Trisagion. The Symbol of Faith. The day’s troparion. Pre-set, ready, waiting.',
    shot: {
      src: '/mobile-screens/prayer.jpg',
      alt: 'Prayer library on the Orthodox Way mobile app',
    },
  },
  {
    time: 'Mid-Morning',
    title: 'The Day’s Saint',
    body: 'Today the Church remembers a saint. Their life, their tropar, their kontakion, three minutes that change how the morning feels.',
    shot: {
      src: '/mobile-screens/today.jpg',
      alt: 'Today screen with saint of the day on the Orthodox Way app',
    },
  },
  {
    time: 'Noon',
    title: 'The Hour',
    body: 'A short prayer at the sixth hour. A glance at today’s Gospel reading. A moment of return.',
    shot: {
      src: '/mobile-screens/readings.jpg',
      alt: 'Daily readings screen on the Orthodox Way mobile app',
    },
  },
  {
    time: 'Afternoon',
    title: 'Fasting Wisdom',
    body: 'Is it a fast day? At what level? What does the Typikon allow? Beginner, standard, strict, clearly. Without guilt.',
    shot: {
      src: '/mobile-screens/fasting.jpg',
      alt: 'Fasting guidance screen on the Orthodox Way mobile app',
    },
  },
  {
    time: 'Evening',
    title: 'Vespers',
    body: 'The day ends as the Church ends it: at sunset, with a hymn to the Light. Service text assembled with today’s variables.',
    shot: {
      src: '/mobile-screens/services.jpg',
      alt: 'Services menu on the Orthodox Way mobile app',
    },
  },
  {
    time: 'Before Sleep',
    title: 'Evening Prayers + Examination',
    body: 'The evening rule. A line to record what you saw of yourself today. No streaks. No scoring. Just witness.',
    shot: {
      src: '/mobile-screens/prayer.jpg',
      alt: 'Evening prayer rule on the Orthodox Way mobile app',
    },
  },
  {
    time: 'The Liturgical Year',
    title: 'The Greater Rhythm',
    body: 'Behind the days runs the year, Pascha, Lent, the Twelve Feasts, the colors of the Octoechos. The app holds all of it.',
    shot: {
      src: '/mobile-screens/today.jpg',
      alt: 'Liturgical-year context on the Orthodox Way Today screen',
    },
  },
];

function Row({ moment, index }: { moment: Moment; index: number }) {
  const textRef = useReveal<HTMLDivElement>(0.25);
  const mockRef = useReveal<HTMLDivElement>(0.25);
  const markerRef = useReveal<HTMLSpanElement>(0.5);
  const isReversed = index % 2 === 1;

  return (
    <div
      className="relative grid lg:grid-cols-[1fr_auto_1fr] gap-8 lg:gap-16 items-center"
      style={{ marginTop: index === 0 ? 0 : 96 }}
    >
      {/* Text column */}
      <div
        ref={textRef}
        className={`ow-reveal-x-left ${isReversed ? 'lg:order-3 ow-reveal-x-right' : 'lg:order-1'}`}
      >
        <p
          className="font-display uppercase text-gold"
          style={{ fontSize: 11, letterSpacing: '0.32em' }}
        >
          {moment.time}
        </p>
        <h3
          className="font-display text-primary mt-3"
          style={{ fontSize: 28, fontWeight: 500, letterSpacing: '0.02em' }}
        >
          {moment.title}
        </h3>
        <p className="text-secondary mt-3 max-w-[460px]" style={{ fontSize: 17, lineHeight: 1.7 }}>
          {moment.body}
        </p>
      </div>

      {/* Timeline marker */}
      <div className="hidden lg:flex order-2 justify-center w-6">
        <span ref={markerRef} className="ow-timeline-marker" />
      </div>

      {/* Mockup */}
      <div
        ref={mockRef}
        className={`ow-reveal-x-right flex justify-center ${
          isReversed ? 'lg:order-1 ow-reveal-x-left' : 'lg:order-3'
        }`}
      >
        <DeviceFrame variant="phone">
          <PhoneShot src={moment.shot.src} alt={moment.shot.alt} />
        </DeviceFrame>
      </div>
    </div>
  );
}

export function DailyRhythm() {
  const headerRef = useReveal<HTMLDivElement>(0.3);
  return (
    <section id="rhythm" className="relative w-full" style={{ padding: '76px 20px' }}>
      <div className="mx-auto max-w-container">
        <div ref={headerRef} className="ow-reveal text-center">
          <p
            className="font-display uppercase text-gold"
            style={{ fontSize: 11, letterSpacing: '0.32em' }}
          >
            § II - The Rhythm
          </p>
          <h2
            className="font-display text-primary mt-5"
            style={{
              fontSize: 'clamp(32px, 5vw, 56px)',
              fontWeight: 500,
              letterSpacing: '0.02em',
            }}
          >
            One Day. Seven Returnings.
          </h2>
          <p
            className="scripture text-secondary mt-4 max-w-[640px] mx-auto"
            style={{ fontSize: 22, lineHeight: 1.5 }}
          >
            The Orthodox day has a shape. Orthodox Way knows it by heart.
          </p>
        </div>

        <div className="relative mt-20">
          {/* Vertical timeline line */}
          <div
            aria-hidden="true"
            className="hidden lg:block absolute top-0 bottom-0 left-1/2 -translate-x-1/2"
            style={{
              width: 1,
              background:
                'linear-gradient(to bottom, transparent, var(--ow-border-strong) 8%, var(--ow-border-strong) 92%, transparent)',
            }}
          />
          {moments.map((m, i) => (
            <Row key={m.title} moment={m} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
